"use client";

import { useRef } from "react";
import Image from "next/image";
import toast from "react-hot-toast";
import { SYMBOL_IMAGES, OFFERED_SYMBOL_IDS } from "@/app/components/appcomponents/thankYouCard";
import ButtonBlueBorder from "@/app/components/appcomponents/ButtonBlueBorder";
import { PopupShell, SecondaryButton } from "./parts";

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

// Blue = "you can change this". The symbol is drawn from its image's shape in that colour.
function BlueSymbol({ symbol }) {
  const mask = `url(${symbol.src}) center / contain no-repeat`;
  return (
    <span
      role="img"
      aria-label={symbol.label}
      className="block h-[56px] w-[46px] mobile:h-[36px] mobile:w-[30px]"
      style={{ backgroundColor: "#0A85C2", WebkitMask: mask, mask }}
    />
  );
}

// Blue = "you can change this". Tablet / desktop: the boxed fields of the desktop design; phone: plain blue text
// (the phone design has no boxes, to leave room on the card).
const inputClass =
  "h-[28px] w-full rounded-[4px] bg-[#F2F8FF] px-2 text-[16px] text-[#0A85C2] shadow-custom-dark-to-white focus:outline-none " +
  "mobile:block mobile:h-[22px] mobile:min-w-0 mobile:rounded-none mobile:border-0 mobile:border-b mobile:border-transparent mobile:bg-transparent mobile:p-0 mobile:text-[14px] mobile:leading-[20px] mobile:shadow-none mobile:focus:border-[#0A85C2]";

/**
 * Step 2: finish the card shown above the message. Blue marks what the author can change:
 * dates, place, photo and religious symbol. "Pregled" shows the finished message.
 */
export default function StepCard({ form, update, onBack, onNext, onPreview, onClose }) {
  const fileInput = useRef(null);
  const { obituary, cardSymbol } = form;
  const photo = form.cardPhotoPreview || obituary.image || "/user5.jpeg";
  const symbol = SYMBOL_IMAGES[cardSymbol];
  // The four symbols of the design, plus the obituary's own symbol if it is one of the older ones.
  const symbolIds =
    cardSymbol && !OFFERED_SYMBOL_IDS.includes(cardSymbol) ? [...OFFERED_SYMBOL_IDS, cardSymbol] : OFFERED_SYMBOL_IDS;

  const handlePhoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      toast.error("Dovoljene so slike JPG, PNG ali WebP");
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Slika je prevelika (največ 5 MB)");
      return;
    }
    if (form.cardPhotoPreview) URL.revokeObjectURL(form.cardPhotoPreview);
    update({ cardPhotoFile: file, cardPhotoPreview: URL.createObjectURL(file) });
  };

  return (
    <PopupShell
      title="Korak 2 - dopolnitev zgornjega dela"
      onClose={onClose}
      flatOnPhone
      titleBelowClose
      figmaDesktop
      panelTopClass="[@media(min-width:744px)]:!mt-[11px]"
      panelPadding="px-5 py-[36px]"
      footer={
        <button
          type="button"
          onClick={onBack}
          className="mt-5 flex items-center gap-3 self-start pl-1 text-[18px] text-[#414B5A] mobile:mt-3 mobile:text-[16px]"
        >
          <span aria-hidden="true" className="text-[26px] leading-none text-[#0A85C2]">
            ‹
          </span>
          Na prejšnjo stran
        </button>
      }
    >
      {/* tablet / desktop: the desktop design */}
      <div className="relative flex gap-4 rounded-[10px] border-2 border-white bg-gradient-to-br from-[#E3EAEF] to-[#F3F6F8] p-4 shadow-custom-light-dark-box mobile:hidden">
        <div className="h-[136px] w-[100px] shrink-0 overflow-hidden rounded-[8px] border-[3px] border-[#0E7FD0]">
          <Image
            src={photo}
            alt={`Fotografija ${obituary.name} ${obituary.sirName}`}
            width={400}
            height={560}
            unoptimized={Boolean(form.cardPhotoPreview)}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start gap-3">
            <div className="min-w-0 max-w-[232px] flex-1">
              <p className="line-clamp-2 text-[24px] leading-[30px] text-[#1E2125]">
                {obituary.name} {obituary.sirName}
              </p>
              <label className="mt-2 block">
                <span className="sr-only">Datumi</span>
                <input type="text" value={form.cardDates} maxLength={40} onChange={(e) => update({ cardDates: e.target.value })} className={inputClass} />
              </label>
              <label className="mt-2 block">
                <span className="sr-only">Kraj</span>
                <input type="text" value={form.cardLocation} maxLength={100} onChange={(e) => update({ cardLocation: e.target.value })} className={inputClass} />
              </label>
            </div>
            <div className="ml-auto flex h-[56px] w-[46px] shrink-0 items-start justify-center">
              {symbol && <BlueSymbol symbol={symbol} />}
            </div>
          </div>
          <p className="mt-1 self-end font-greatVibes text-[20px] leading-[26px] text-[#414B5A]">Za vedno v naših srcih</p>
        </div>
      </div>

      {/* phone: the phone design */}
      <div className="relative hidden gap-3 rounded-[10px] border-2 border-white bg-gradient-to-br from-[#E3EAEF] to-[#F3F6F8] p-3 shadow-custom-light-dark-box mobile:flex">
        <div className="h-[98px] w-[72px] shrink-0 overflow-hidden rounded-[8px] border-[3px] border-[#0E7FD0]">
          <Image
            src={photo}
            alt={`Fotografija ${obituary.name} ${obituary.sirName}`}
            width={400}
            height={560}
            unoptimized={Boolean(form.cardPhotoPreview)}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          {/* the sign takes the corner; only the name keeps clear of it */}
          <div className="flex items-start gap-2">
            <p className="min-w-0 flex-1 text-[16px] font-semibold leading-[20px] text-[#1E2125]">
              {obituary.name} {obituary.sirName}
            </p>
            <div className="flex h-[36px] w-[30px] shrink-0 items-start justify-center">
              {symbol && <BlueSymbol symbol={symbol} />}
            </div>
          </div>
          <label className="mt-1 block">
            <span className="sr-only">Datumi</span>
            <input type="text" value={form.cardDates} maxLength={40} onChange={(e) => update({ cardDates: e.target.value })} className={inputClass} />
          </label>
          <label className="mt-1 block">
            <span className="sr-only">Kraj</span>
            <input type="text" value={form.cardLocation} maxLength={100} onChange={(e) => update({ cardLocation: e.target.value })} className={inputClass} />
          </label>
          <p className="mt-auto self-end pt-2 font-greatVibes text-[15px] leading-[20px] text-[#414B5A]/80">Za vedno v naših srcih</p>
        </div>
      </div>

      <p className="mt-3 whitespace-nowrap text-[15px] leading-[20px] text-[#0A85C2] mobile:text-[14px]">
        <span className="mobile:hidden">
          * lahko spremenite, dopolnite. Enako tudi sliko in religiozni simbol
        </span>
        <span className="hidden mobile:inline">* Modra barva označuje, kar lahko spremenite.</span>
      </p>

      <div className="mt-5 flex items-center gap-4">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="h-[40px] w-[150px] rounded-[6px] bg-gradient-to-b from-[#0E9DE3] to-[#0773C3] text-[16px] text-white shadow-md"
        >
          Dodaj sliko
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handlePhoto}
        />
        <span className="text-[12px] text-[#939393] mobile:hidden">jpg, png, webp · do 5 MB</span>
      </div>

      <div className="mt-6 flex justify-end mobile:justify-start">
        <label className="flex cursor-pointer items-center gap-3 rounded-[6px] bg-[#F2F8FF66] px-4 py-3 text-[16px] text-[#6D778E] shadow-custom-dark-to-white mobile:w-full">
          <input
            type="checkbox"
            checked={cardSymbol !== ""}
            onChange={(e) => update({ cardSymbol: e.target.checked ? "1" : "" })}
            className="h-5 w-5"
          />
          Dodaj religiozni simbol
        </label>
      </div>

      {cardSymbol !== "" && (
        <div role="radiogroup" aria-label="Religiozni simbol" className="mt-4 flex flex-wrap items-center justify-end gap-x-3 gap-y-2 mobile:justify-between">
          {symbolIds.map((id) => {
            const item = SYMBOL_IMAGES[id];
            const selected = cardSymbol === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={item.label}
                onClick={() => update({ cardSymbol: id })}
                className={`relative flex h-[76px] w-[64px] items-center justify-center rounded-[6px] mobile:h-[70px] mobile:w-[56px] ${
                  selected ? "bg-[#F2F8FF66] shadow-custom-dark-to-white" : ""
                }`}
              >
                <Image src={item.src} alt="" width={44} height={54} loading="eager" className="h-[54px] w-[44px] object-contain" />
                {selected && (
                  <span aria-hidden="true" className="absolute bottom-[3px] right-[5px] text-[14px] font-bold leading-none text-[#EB1D1D]">
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
      <p className="mt-3 whitespace-nowrap text-right text-[13px] leading-[18px] text-[#3C3E41] mobile:text-left mobile:text-[12px]">
        Prikazan bo v seznamu osmrtnic in na spominski strani.
      </p>

      {/* tablet / desktop */}
      <div className="mt-8 flex items-center justify-between gap-4 mobile:hidden">
        <SecondaryButton onClick={onPreview} className="h-[60px] px-5 text-[18px]">
          Pregled
        </SecondaryButton>
        <div className="w-[210px]">
          <ButtonBlueBorder placeholder="Naprej" onClick={onNext} />
        </div>
      </div>
      {/* phone */}
      <div className="mt-6 hidden items-center justify-between gap-4 mobile:flex">
        <SecondaryButton onClick={onPreview} className="h-[48px] px-6 text-[18px]">
          Pregled
        </SecondaryButton>
        <button
          type="button"
          onClick={onNext}
          className="h-[52px] w-[168px] rounded-[10px] bg-gradient-to-b from-[#0A85C2] to-[#123597] p-[2px] shadow-custom-dark-bottom"
        >
          <span className="flex h-full w-full items-center justify-center rounded-[8px] bg-[#E7EBF0] font-variation-customOpt20 text-[18px] font-semibold leading-[24px] text-[#414B5A]">
            Naprej
          </span>
        </button>
      </div>
    </PopupShell>
  );
}
